import { Controller } from "@hotwired/stimulus"
import { cameraHasOpened } from "../camera_nfc_conflict"
import { nfcPermissionGranted } from "../nfc_permission"

// Chrome for Android only — Web NFC (NDEFReader) is not available elsewhere.
// Reads a tag and dispatches a bubbling "nfc:read" { code, serial }, mirroring how
// qr-scanner dispatches "qr:read" { code }. What to do with the read is the
// consumer's call.
//
// `code` is what any code field wants — the tag's NDEF text payload when it has
// one, its serial otherwise — and carrying it under the same name qr:read uses
// means a field can bind both events to one handler. `serial` is there for the chip
// writer, which stores it as chip_uid and must not accept a text payload in its
// place.
//
// Every "reading" is dispatched — a card left resting on the reader keeps firing,
// and deduplicating is the consumer's business. Deliberately not one-shot: the
// writer must keep reading after a failed POST, other consumers need not.
//
// The QR camera on the same screen kills this reader for the rest of the
// document's life — see camera_nfc_conflict for what was measured and what does
// not fix it. Nothing here can undo that, so the reader stops pretending: it
// disarms and says why, which is the difference between an operator switching to
// the QR and an operator tapping a card against a phone that stopped listening.
//
// Values:
//   required — true where NFC is the only way through the screen, so a browser
//              without Web NFC gets told; false where it is one route among typing
//              and QR, where the news is noise nobody can act on
// Targets:
//   startBtn — shown only when the permission still has to be asked for
//   status   — carries the reason the reader could not arm
export default class extends Controller {
  static values = { required: Boolean }
  static targets = ["startBtn", "status"]

  #abortController = null
  #onCameraOpened = () => this.#surrender()

  async connect() {
    if (!("NDEFReader" in window)) {
      if (this.requiredValue) this.#status("Leitura NFC indisponível (requer Chrome para Android).")
      return
    }

    window.addEventListener("camera:opened", this.#onCameraOpened)

    // A camera opened earlier in this document — on this screen or on one Turbo
    // navigated away from — and there is nothing left to arm.
    if (cameraHasOpened()) {
      this.#surrender()
      return
    }

    // Chrome persists the NFC grant per origin, and scan() only needs a gesture to
    // raise the prompt — once granted it arms from anywhere, including here. So the
    // button is a once-per-device cost rather than a once-per-read one.
    if (await nfcPermissionGranted()) {
      this.start()
    } else {
      this.startBtnTarget.hidden = false
    }
  }

  disconnect() {
    window.removeEventListener("camera:opened", this.#onCameraOpened)
    this.#abortController?.abort()
    this.#abortController = null
  }

  // `scan()` MUST run inside the user's tap while the permission is still "ask":
  // the prompt only opens under transient user activation. Called without one it
  // rejects with NotAllowedError, reader mode never comes up, and Android's own tag
  // dispatch grabs the card instead ("New tag scanned" / "Empty tag").
  async start() {
    // connect() checks this too, but it checks before awaiting the permission
    // query: a camera opened inside that await would otherwise arm a dead reader
    // here, and the `scan()` resolution below would wipe the warning off the
    // screen on its way out.
    if (cameraHasOpened()) return this.#surrender()

    this.startBtnTarget.hidden = true
    this.#abortController = new AbortController()

    try {
      const reader = new NDEFReader()

      // Registered before the await: a card already resting on the reader fires
      // "reading" the moment reader mode comes up, and a listener added after the
      // await misses that first event.
      reader.addEventListener("reading", (event) => this.#dispatch(event))

      await reader.scan({ signal: this.#abortController.signal })

      this.statusTarget.hidden = true
    } catch (error) {
      this.startBtnTarget.hidden = false
      this.#status(this.#errorMessage(error))
    }
  }

  // private

  // Reading is over for this document. The tap is taken away along with the
  // reader — leaving a button that can only fail is worse than no button.
  #surrender() {
    this.#abortController?.abort()
    this.#abortController = null
    this.startBtnTarget.hidden = true
    this.#status("A câmera desativou a leitura NFC neste aparelho. Recarregue a página para ler chips de novo.")
  }

  #dispatch({ message, serialNumber }) {
    const serial = serialNumber ? serialNumber.replaceAll(":", "").toLowerCase() : null

    this.element.dispatchEvent(new CustomEvent("nfc:read", {
      bubbles: true,
      detail: { code: this.#textRecord(message) || serial, serial }
    }))
  }

  #textRecord(message) {
    const record = message?.records?.find((r) => r.recordType === "text")
    return record ? new TextDecoder().decode(record.data) : null
  }

  // scan() rejects with a DOMException, so `name` is always there — no optional
  // chaining, unlike html5-qrcode, which rejects with a bare string.
  #errorMessage(error) {
    switch (error.name) {
      case "NotAllowedError":
        return 'Permissão de NFC negada. Toque de novo e escolha "Permitir".'
      case "NotReadableError":
        return "NFC desligado. Ative o NFC nas configurações do Android e toque de novo."
      case "NotSupportedError":
        return "Este aparelho não tem NFC."
      default:
        return `Falha ao iniciar a leitura NFC (${error.name}). Toque de novo.`
    }
  }

  #status(message) {
    this.statusTarget.textContent = message
    this.statusTarget.hidden = false
  }
}
